import domtoimage from 'dom-to-image';
import { createLogger } from '../services/Debug';
/**
 * Captures the stress bullseye from the existing DOM element
 * This ensures the exported image matches what the user sees exactly
 * @param {Object} options - Rendering options
 * @returns {Promise<string|null>} Base64 data URL or null if element not found
 */
export async function captureStressBullseyeFromDOM(options = {}) {
  const log = createLogger('captureStressBullseyeFromDOM');
  const node = document.getElementById('stress-bullseye-wrapper');
  if (!node) {
    return null;
  }

  // Increase scale for better quality in Word export
  const scale = options.scale || 4;
  const outputDownscaleFactor = typeof options.outputDownscaleFactor === 'number'
    ? options.outputDownscaleFactor
    : 1;

  try {
    // Get actual dimensions
    const rect = node.getBoundingClientRect();
    const exportWidth = Math.max(rect.width, node.scrollWidth || 0, node.offsetWidth || 1100);
    const exportHeight = Math.max(rect.height, node.scrollHeight || 0, node.offsetHeight || 600);

    // Use device pixel ratio for high-DPI displays
    const pixelRatio = window.devicePixelRatio || 1;

    const dataUrl = await domtoimage.toPng(node, {
      width: exportWidth * scale,
      height: exportHeight * scale,
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        backgroundColor: '#ffffff'
      },
      quality: 1,
      bgcolor: '#ffffff',
      imagePlaceholder: undefined,
      cacheBust: true
    });

    // Optionally downscale
    if (outputDownscaleFactor > 0 && outputDownscaleFactor < 1) {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(img.width * outputDownscaleFactor));
      canvas.height = Math.max(1, Math.floor(img.height * outputDownscaleFactor));
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    }

    return dataUrl;
  } catch (error) {
    log.error('Error capturing stress bullseye from DOM:', error);
    return null;
  }
}

/**
 * Captures a standard bullseye from the existing DOM element
 * @param {Object} options - Rendering options
 * @param {string} elementId - ID of the element to capture (default: 'bullseye-wrapper')
 * @returns {Promise<string|null>} Base64 data URL or null if element not found
 */
export async function captureStandardBullseyeFromDOM(options = {}, elementId = 'bullseye-wrapper') {
  const node = document.getElementById(elementId);
  const log = createLogger('captureStandardBullseyeFromDOM');
  if (!node) {
    return null;
  }

  // Increase scale for better quality in Word export
  const scale = options.scale || 4;
  const outputDownscaleFactor = typeof options.outputDownscaleFactor === 'number'
    ? options.outputDownscaleFactor
    : 1;

  try {
    // Get actual dimensions
    const rect = node.getBoundingClientRect();
    const exportWidth = Math.max(rect.width, node.scrollWidth || 0, node.offsetWidth || 280);
    const exportHeight = Math.max(rect.height, node.scrollHeight || 0, node.offsetHeight || 280);

    // Use device pixel ratio for high-DPI displays
    const pixelRatio = window.devicePixelRatio || 1;

    const dataUrl = await domtoimage.toPng(node, {
      width: exportWidth * scale,
      height: exportHeight * scale,
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        backgroundColor: '#ffffff'
      },
      quality: 1,
      bgcolor: '#ffffff',
      imagePlaceholder: undefined,
      cacheBust: true
    });

    // Optionally downscale
    if (outputDownscaleFactor > 0 && outputDownscaleFactor < 1) {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(img.width * outputDownscaleFactor));
      canvas.height = Math.max(1, Math.floor(img.height * outputDownscaleFactor));
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    }

    return dataUrl;
  } catch (error) {
    log.error('Error capturing standard bullseye from DOM:', error);
    return null;
  }
}

/**
 * Captures the IRM Cardiaque bullseye from the existing DOM element
 * @param {Object} options - Rendering options
 * @returns {Promise<string|null>} Base64 data URL or null if element not found
 */
export async function captureIRMBullseyeFromDOM(options = {}) {
    const node = document.getElementById('irm-bullseye-wrapper');
  const log = createLogger('captureIRMBullseyeFromDOM');
  if (!node) {
    return null;
  }

  // Use higher scale for better quality (increased from 3 to 5)
  const scale = options.scale || 5;
  const outputDownscaleFactor = typeof options.outputDownscaleFactor === 'number'
    ? options.outputDownscaleFactor
    : 1;

  try {
    // Force layout recalculation
    void node.offsetHeight;
    
    // Get actual dimensions with all measurements
    const rect = node.getBoundingClientRect();
    const scrollWidth = node.scrollWidth || 0;
    const scrollHeight = node.scrollHeight || 0;
    const offsetWidth = node.offsetWidth || 0;
    const offsetHeight = node.offsetHeight || 0;
    
    // Use the maximum of all measurements
    // IRM has 3 bullseyes of 260x260 + titles + legend + padding
    // Expected width: 3 * 260 + 2 * 30 (gap) + 2 * 20 (padding) = 880
    // Expected height: 260 + title + legend + padding = ~400
    const exportWidth = Math.max(rect.width, scrollWidth, offsetWidth, 880);
    const exportHeight = Math.max(rect.height, scrollHeight, offsetHeight, 400);

    // Use device pixel ratio for high-DPI displays
    const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);

    // Capture at high quality
    const dataUrl = await domtoimage.toPng(node, {
      width: exportWidth * pixelRatio,
      height: exportHeight * pixelRatio,
      style: {
        backgroundColor: '#ffffff',
        transform: `scale(${pixelRatio})`,
        transformOrigin: 'top left',
        width: exportWidth + 'px',
        height: exportHeight + 'px'
      },
      quality: 1,
      bgcolor: '#ffffff',
      filter: (node) => {
        // Ensure text and graphics are sharp
        if (node.style) {
          node.style.imageRendering = 'crisp-edges';
        }
        return true;
      }
    });
    
    // Scale up using canvas for even higher quality if needed
    if (scale > pixelRatio) {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = exportWidth * scale;
      canvas.height = exportHeight * scale;
      const ctx = canvas.getContext('2d', { alpha: false });
      
      // Set highest quality settings
      ctx.imageSmoothingEnabled = false; // Disable smoothing for sharper output
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw at scale for maximum sharpness
      ctx.save();
      ctx.scale(scale / pixelRatio, scale / pixelRatio);
      ctx.drawImage(img, 0, 0);
      ctx.restore();
      
      const scaledDataUrl = canvas.toDataURL('image/png', 1.0);
      
      // Apply downscale if needed
      if (outputDownscaleFactor > 0 && outputDownscaleFactor < 1) {
        const downscaleCanvas = document.createElement('canvas');
        downscaleCanvas.width = Math.max(1, Math.floor(canvas.width * outputDownscaleFactor));
        downscaleCanvas.height = Math.max(1, Math.floor(canvas.height * outputDownscaleFactor));
        const downscaleCtx = downscaleCanvas.getContext('2d');
        downscaleCtx.imageSmoothingEnabled = true;
        downscaleCtx.imageSmoothingQuality = 'high';
        downscaleCtx.drawImage(canvas, 0, 0, downscaleCanvas.width, downscaleCanvas.height);
        return downscaleCanvas.toDataURL('image/png');
      }
      
      return scaledDataUrl;
    }

    // Apply downscale to original if needed (when scale = 1)
    if (outputDownscaleFactor > 0 && outputDownscaleFactor < 1) {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(img.width * outputDownscaleFactor));
      canvas.height = Math.max(1, Math.floor(img.height * outputDownscaleFactor));
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    }

    return dataUrl;
  } catch (error) {
    log.error('Error capturing IRM bullseye from DOM:', error);
    return null;
  }
}